/**
 * Smart Banner Error Class
 * Custom error handling for SmartBanner operations
 */

export class SmartBannerError extends Error {
  constructor(message, code = "UNKNOWN_ERROR", context = null) {
    super(message);
    this.name = "SmartBannerError";
    this.code = code;
    this.context = context;
    this.timestamp = new Date().toISOString();

    // Maintains proper stack trace (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SmartBannerError);
    }
  }

  /**
   * Convert error to JSON for logging
   * @returns {Object} JSON representation of error
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack,
    };
  }

  /**
   * Create API error
   * @param {string} message - Error message
   * @param {Object} context - Additional context
   * @returns {SmartBannerError} API error instance
   */
  static apiError(message, context = null) {
    return new SmartBannerError(message, "API_ERROR", context);
  }

  /**
   * Create validation error
   * @param {string} message - Error message
   * @param {Object} context - Additional context
   * @returns {SmartBannerError} Validation error instance
   */
  static validationError(message, context = null) {
    return new SmartBannerError(message, "VALIDATION_ERROR", context);
  }

  /**
   * Create render error
   * @param {string} message - Error message
   * @param {Object} context - Additional context
   * @returns {SmartBannerError} Render error instance
   */
  static renderError(message, context = null) {
    return new SmartBannerError(message, "RENDER_ERROR", context);
  }

  /**
   * Create timeout error
   * @param {string} message - Error message
   * @param {Object} context - Additional context
   * @returns {SmartBannerError} Timeout error instance
   */
  static timeoutError(message, context = null) {
    return new SmartBannerError(message, "API_TIMEOUT", context);
  }
}
